import { useQuery } from "@tanstack/react-query";
import { DataLakes } from "@graylog/enterprise-api";
import FieldTypeMapping from "views/logic/fieldtypes/FieldTypeMapping";
import { FieldTypes } from "views/logic/fieldtypes/FieldType";
import useSelectedDataLake from "data-lake/hooks/useSelectedDataLake";
import { INTERNAL_DATA_LAKE_ID } from "data-lake/preview/storage-types/internal/Constants";
import { defaultCompare } from "logic/DefaultCompare";
const NON_FILTERABLE_FIELD_TYPES = [FieldTypeMapping.create("timestamp", FieldTypes.DATE())];
const mapBackendType = (fieldType) => {
  switch (fieldType) {
    case "org.joda.time.DateTime":
      return "date";
    case "java.lang.Integer":
      return "int";
    case "java.lang.Boolean":
      return "boolean";
    case "java.lang.String":
      return "string";
    default:
      return "unknown";
  }
};
const identifyFieldType = (fieldType, fieldName) => {
  if (fieldName === "streams") {
    return "streams";
  }
  if (fieldName === "gl2_source_input") {
    return "input";
  }
  if (fieldName === "associated_assets") {
    return "associated-assets";
  }
  if (["source_ip", "associated_ip", "destination_ip"].includes(fieldName)) {
    return "ip";
  }
  return mapBackendType(fieldType);
};
const deserializeFieldTypes = (fieldTypes = []) => fieldTypes.map(
  ({ field_name: fieldName, class: fieldType }) => FieldTypeMapping.fromJSON({
    name: fieldName,
    type: {
      type: identifyFieldType(fieldType, fieldName),
      properties: ["enumerable"],
      index_names: []
    }
  })
);
const fetchFields = async (dataLakeId, tableId) => {
  var _a, _b, _c;
  const nonDeserializedData = await DataLakes.getFields(dataLakeId, tableId);
  const notFilterableFieldsSet = new Set(
    nonDeserializedData == null ? void 0 : nonDeserializedData.filter(({ filtering }) => filtering === "NONE").map(({ field_name }) => field_name)
  );
  const data = deserializeFieldTypes(nonDeserializedData).sort((a, b) => defaultCompare(a.name, b.name));
  const widgetFields = (_a = data == null ? void 0 : data.filter((field) => !NON_FILTERABLE_FIELD_TYPES.find((nonFilterable) => nonFilterable.name === field.name))) != null ? _a : [];
  const filterableFields = (_b = widgetFields == null ? void 0 : widgetFields.filter((field) => !notFilterableFieldsSet.has(field.name))) != null ? _b : [];
  const inMemoryFields = new Set(
    (_c = nonDeserializedData == null ? void 0 : nonDeserializedData.filter(({ filtering }) => filtering === "IN_MEMORY").map(({ field_name }) => field_name)) != null ? _c : []
  );
  return {
    allFieldTypes: data ? [...NON_FILTERABLE_FIELD_TYPES, ...data] : [],
    widgetFields,
    filterableFields,
    inMemoryFields
  };
};
const useFieldTypes = (fetchQueryOverride, enabled = true) => {
  const { selectedDataLake, dataLakeFieldsFetchQuery } = useSelectedDataLake();
  const fetchQuery = fetchQueryOverride != null ? fetchQueryOverride : dataLakeFieldsFetchQuery;
  const dataLakeId = selectedDataLake == null ? void 0 : selectedDataLake.id;
  const {
    data: { allFieldTypes, filterableFields, widgetFields, inMemoryFields },
    refetch,
    isLoading,
    isPending
  } = useQuery({
    queryKey: ["data-lake", "filterable-fields", dataLakeId, fetchQuery == null ? void 0 : fetchQuery.tableId],
    queryFn: () => fetchFields(dataLakeId, fetchQuery == null ? void 0 : fetchQuery.tableId),
    staleTime: 3e4,
    enabled: enabled && !!dataLakeId && (dataLakeId === INTERNAL_DATA_LAKE_ID || !!(fetchQuery == null ? void 0 : fetchQuery.tableId)),
    placeholderData: { filterableFields: [], allFieldTypes: [], inMemoryFields: /* @__PURE__ */ new Set(), widgetFields: [] }
  });
  return {
    data: allFieldTypes,
    filterableFields,
    refetch,
    isLoading,
    isPending,
    inMemoryFields,
    widgetFields
  };
};
export default useFieldTypes;
